import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Grid, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatDate } from '../../../utils/formatDate';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import { useGet, useGetBlob } from '../../../sharedModules/hooks/useQuery';

interface InspectionReport {
  id: number;
  name?: string;
  technician?: string;
  date?: string;
  order_no?: string;
  pdf_url?: string;
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { data?: { error?: string; message?: string } } };
    return err?.response?.data?.error || err?.response?.data?.message || 'Failed to load report';
  }
  return 'Failed to load report';
}

const InspectionReportShowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useGet<InspectionReport>({
    url: `/home_services/inspection_reports/${id}`,
  });

  const { data: pdfResponse } = useGetBlob({
    url: `/home_services/inspection_reports/${id}/pdf`,
  });

  const report = data?.data ?? null;
  const errorMessage = error ? getErrorMessage(error) : '';

  const pdfBlob = pdfResponse?.data;
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfBlob) {
      setPdfBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    setPdfBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pdfBlob]);

  return (
    <LoadingWrapper isLoading={isLoading}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <PageHeader
          mainTitle="Inspection Report"
          subTitle={report ? `${report.name || 'Report'} #${id}` : `#${id}`}
          backButtonLabel="Back to Reports"
          backButtonLink="/home_services/reports"
        />

        {errorMessage && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}

        {report && (
          <>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{report.name || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Technician
                </Typography>
                <Typography variant="body1">{report.technician || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1">{formatDate(report.date) || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Order Number
                </Typography>
                <Typography variant="body1">{report.order_no || '—'}</Typography>
              </Grid>
            </Grid>

            {report.pdf_url && (
              <Box sx={{ mt: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    InspectionReport PDF
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={report.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open PDF
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 400, md: 600 },
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'grey.300',
                  }}
                >
                  {pdfBlobUrl ? (
                    <iframe
                      src={pdfBlobUrl}
                      title="Inspection Report PDF"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body2">Loading PDF…</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </LoadingWrapper>
  );
};

export default InspectionReportShowPage;
